/**
 * 增强API服务模块
 * 为原始API模块添加统一的日期时间处理、错误处理和日志记录功能
 *
 * @规范: 所有API返回应当遵循以下标准结构:
 * 1. 列表数据: { items: Array, total: number, [metadata] }
 * 2. 单个资源: 直接返回资源对象
 *
 * @时区处理规范:
 * 1. 所有API响应的时间字段必须标准化为带Z后缀的UTC格式
 * 2. 前端状态管理层统一使用UTC格式存储时间，仅在UI展示层转换为用户选择的时区
 * 3. 编辑表单必须使用UTC系列组件，确保提交到后端的数据始终为UTC格式
 * 4. 业务计算逻辑(如宵禁检查、连接时间计算)必须使用UTC时间进行
 *
 * 增强后的API服务处理不同的后端响应格式，确保向前端组件提供一致的数据结构
 */
import { enhanceApiModule } from "./apiUtils";
import { masterDataApi } from "./masterDataApi";
import { scheduleApi } from "./scheduleApi";
import scheduleEmailApi from "./scheduleEmailApi";
import flightLegApi from "./flightLegApi";
import aiActionItemApi from "./aiActionItemApi";
import { updateActionItem } from "./actionItemApi";
import flightOperationFlightApi from "./flightOperationFlightApi";
import flightOperationEventLogApi from "./flightOperationEventLogApi";
import flightOperationTransactionApi from "./flightOperationTransactionApi";
import * as userManagementApi from "./userManagement";
import { emailTemplateApi } from "./emailTemplateApi";
import userEmailConfigApi from "./userEmailConfigApi";
import { contractManagementApi } from "./contractManagementApi";
import { vendorCrmApi } from "./vendorCrmApi";
import emailGroupApi from "./emailGroupApi";
import { attachmentApi } from "./attachmentApi";
import uldApi from "./uldApi";
import { uldDeliveryApi } from "./uldDeliveryApi";
import { uldStockAlertApi } from "./uldStockAlertApi";
import ucmApi from "./ucmApi";
import scmApi from "./scmApi";
import { permitRequestApi } from "./permitRequestApi";
import { flightPlannerApi } from "./flightPlannerApi";
import batchTransactionApiDefault, { batchTransactionV3Api } from "./batchTransactionApi";
import { batchTransactionV4Api } from "./batchTransactionV4Api";
import { flightRouteApi } from "./flightRouteApi";
import { settlementApi } from "./settlementApi";
import { exchangeRateApi } from "./exchangeRateApi";
import { scenarioContextApi } from "./scenarioContextApi";
import { agentVariantApi } from "./agentVariantApi";
import { dashboardApi } from "./dashboardApi";
import { screenApi } from "./screenApi";
import mailboxApi from "./mailboxApi";
import flightOperationStatsApi from "./flightOperationStatsApi";
import { mawbStockApi, mawbUsageApi } from "./mawbApi";

/**
 * 增强版航段API
 */
export const flightLegService = enhanceApiModule(flightLegApi, {
    detectNested: true,
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版航班计划API
 * - 自动检测并标准化日期时间字段为带 Z 的 UTC 格式
 * - 确保状态层存储的是统一的 UTC 时间
 */
export const scheduleService = enhanceApiModule(scheduleApi, {
    // 这里可以指定额外的处理选项
    // 如果不指定，将自动检测和处理常见日期字段
    detectNested: true,
    dateFields: ["start_date", "end_date", "flight_date", "std", "sta", "updated_at", "created_at"],
    convertToLocal: false, // IMPORTANT: 禁用本地时间转换，保持 UTC 格式
});

/**
 * 增强版航班计划邮件API
 * 处理航班计划确认邮件的发送和历史查询
 */
export const scheduleEmailService = enhanceApiModule(scheduleEmailApi, {
    detectNested: true,
    dateFields: ["sent_at", "created_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版主数据API
 */
export const masterDataService = enhanceApiModule(
    {
        ...masterDataApi,
        flightLeg: flightLegApi,
        emailGroup: emailGroupApi.emailGroup,
        emailAddress: emailGroupApi.emailAddress,
        exchangeRate: exchangeRateApi,
    },
    {
        detectNested: true,
        dateFields: ["effective_from", "effective_to", "last_update", "created_at", "updated_at"],
        convertToLocal: false, // 禁用本地时间转换
    },
);

/**
 * 增强版航班运行API
 * 支持统一变更命令(v2.0)处理模式 - 通过变更分发器处理所有修改操作
 */
export const flightOperationFlightService = enhanceApiModule(flightOperationFlightApi, {
    detectNested: true,
    dateFields: ["flight_date", "std", "sta", "etd", "eta", "atd", "ata", "created_at", "updated_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版航班事件日志API
 */
export const flightOperationEventLogService = enhanceApiModule(flightOperationEventLogApi, {
    detectNested: true,
    dateFields: ["created_at", "log_time"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版事务API
 * 处理航班相关事务(许可申请等)，支持直接API调用和统一变更命令模式
 */
export const flightOperationTransactionService = enhanceApiModule(flightOperationTransactionApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at", "request_date", "completion_date"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版AI待办事项API
 * 端点: `/v1/action_items`，已与后端保持一致
 */
export const aiActionItemService = enhanceApiModule(
    {
        ...aiActionItemApi,
        updateActionItem, // 添加更新方法
    },
    {
        detectNested: true,
        dateFields: ["created_at", "updated_at", "suggested_value", "current_value"],
        convertToLocal: false, // 禁用本地时间转换
    },
);

/**
 * 增强版用户管理API
 * 处理用户、角色、权限相关操作
 */
export const userManagementService = enhanceApiModule(userManagementApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at", "last_login"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版邮件模板API
 * 处理邮件模板的CRUD操作、分类管理和标签管理
 */
export const emailTemplateService = enhanceApiModule(emailTemplateApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at", "last_used_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版用户邮箱配置API
 * 处理用户邮箱配置的CRUD操作、测试连接和默认邮箱设置
 */
export const userEmailConfigService = enhanceApiModule(userEmailConfigApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版合同管理API
 * 处理业务伙伴、产品、供应商合同、产品套装和服务订单的CRUD操作
 */
export const contractManagementService = enhanceApiModule(contractManagementApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at", "valid_from", "valid_to"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 供应商 CRM。字典只读；主档读写走 /v3/contract/vendor-crm。
 */
export const vendorCrmService = enhanceApiModule(vendorCrmApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at", "valid_from", "valid_to", "happened_at"],
    convertToLocal: false,
});

/**
 * 增强版邮箱地址与分组API
 * 处理邮箱分组和邮箱地址的CRUD操作、标签管理
 */
export const emailGroupService = enhanceApiModule(emailGroupApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版附件管理API
 * 处理文件上传、附件生成和管理功能
 */
export const attachmentService = enhanceApiModule(attachmentApi, {
    detectNested: true,
    dateFields: ["expires_at", "last_modified"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版ULD管理API
 * 处理ULD基础信息、库存、位置、状态、标签等操作
 */
export const uldService = enhanceApiModule(uldApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at", "manufacture_date", "purchase_date", "lease_start_date", "lease_end_date", "last_moved_at", "last_status_changed_at", "event_timestamp", "assigned_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版ULD外放管理API
 * 处理ULD外放给收货人的相关操作
 */
export const uldDeliveryService = enhanceApiModule(uldDeliveryApi, {
    detectNested: true,
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版ULD库存预警API
 * 处理ULD库存预警配置和监控列表
 */
export const uldStockAlertService = enhanceApiModule(uldStockAlertApi, {
    detectNested: true,
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版UCM报文生成API
 * 处理UCM报文生成和历史记录管理
 */
export const ucmService = enhanceApiModule(ucmApi, {
    detectNested: true,
    dateFields: ["flight_date", "created_at", "confirmed_at"],
    convertToLocal: false,
});

/**
 * 增强版SCM报文解析API
 */
export const scmService = enhanceApiModule(scmApi, {
    detectNested: true,
    dateFields: ["report_time", "created_at", "confirmed_at"],
    convertToLocal: false,
});

/**
 * 增强版许可申请API
 * 处理航班许可申请的查询、创建、更新等操作
 */
export const permitRequestService = enhanceApiModule(permitRequestApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at", "flight_date", "std", "sta"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版航班规划器API
 * 基于ALNS算法的智能航班规划服务
 */
export const flightPlannerService = enhanceApiModule(flightPlannerApi, {
    detectNested: true,
    dateFields: ["departure_time", "arrival_time"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版批量事务V3.0 API
 * 支持基于明细表的产品驱动事务处理
 */
export const batchTransactionV3Service = enhanceApiModule(
    { ...batchTransactionV3Api, ...batchTransactionApiDefault },
    {
        detectNested: true,
        dateFields: ["created_at", "updated_at", "scheduled_time", "completed_time"],
        convertToLocal: false, // 禁用本地时间转换
    },
);

/**
 * 增强版批量事务V4.0 API
 * 基于客户端状态追踪模式的统一批量处理
 */
export const batchTransactionV4Service = enhanceApiModule(batchTransactionV4Api, {
    detectNested: true,
    dateFields: ["created_at", "updated_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版航路API
 */
export const flightRouteService = enhanceApiModule(flightRouteApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版结算管理API
 */
export const settlementService = enhanceApiModule(settlementApi, {
    detectNested: false, // 禁用嵌套处理，避免丢失小节币种和汇率信息
    dateFields: [],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版场景化上下文管理API
 */
export const scenarioContextEnhancedService = enhanceApiModule(scenarioContextApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版Agent变体路由规则管理API
 */
export const agentVariantEnhancedService = enhanceApiModule(agentVariantApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版 Dashboard API
 */
export const dashboardService = enhanceApiModule(dashboardApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版运营大屏配置管理API
 */
export const screenService = enhanceApiModule(screenApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at", "effective_date", "next_inspection_date"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * 增强版邮箱管理API
 * 处理发件箱、收件箱等邮件列表相关操作
 */
export const mailboxService = enhanceApiModule(mailboxApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at", "sent_at", "date"],
    convertToLocal: false, // 禁用本地时间转换
});

/**
 * API服务导出
 * 这些服务现在只负责从 API 获取数据，并由 axios 拦截器确保时间格式为带 Z 的 UTC
 * 状态层将直接存储此 UTC 格式
 */

const enhancedApiServices = {
    masterData: masterDataService,
    flightLeg: flightLegService,
    flightRoute: flightRouteService,
    schedule: scheduleService,
    scheduleEmail: scheduleEmailService,
    aiActionItem: aiActionItemService,
    actionItem: aiActionItemService, // 别名，保持兼容性
    flightOperationFlight: flightOperationFlightService,
    flightOperationEventLog: flightOperationEventLogService,
    flightOperationTransaction: flightOperationTransactionService,
    userManagement: userManagementService,
    emailTemplate: emailTemplateService,
    userEmailConfig: userEmailConfigService,
    contractManagement: contractManagementService,
    vendorCrm: vendorCrmService,
    emailGroup: emailGroupService.emailGroup,
    emailAddress: emailGroupService.emailAddress,
    attachment: attachmentService,
    uld: uldService,
    uldDelivery: uldDeliveryService,
    uldStockAlert: uldStockAlertService,
    ucm: ucmService,
    scm: scmService,
    permitRequest: permitRequestService,
    flightPlanner: flightPlannerService,
    batchTransactionV3: batchTransactionV3Service,
    batchTransactionV4: batchTransactionV4Service,
    settlement: settlementService,
    scenarioContext: scenarioContextEnhancedService,
    agentVariant: agentVariantEnhancedService,
    dashboard: dashboardService,
    screen: screenService,
    mailbox: mailboxService,
};

export default enhancedApiServices;

/**
 * 增强版主单号管理API
 */
export const mawbStockService = enhanceApiModule(mawbStockApi, {
    detectNested: true,
    dateFields: ["created_at", "updated_at", "received_date"],
    convertToLocal: false,
});

export const mawbUsageService = enhanceApiModule(mawbUsageApi, {
    detectNested: true,
    dateFields: ["created_at", "used_at"],
    convertToLocal: false,
});

/**
 * 增强版航班运营统计API
 */
export const flightOperationStatsService = enhanceApiModule(flightOperationStatsApi, {
    detectNested: true,
    dateFields: ["flight_date"],
    convertToLocal: false, // 禁用本地时间转换
});
